/**
 * Ruler passive ability effect
 */
export interface RulerPassive {
  name: string;
  description: string;
  // Stat bonuses applied to all units
  teamBonuses?: {
    attackPercent?: number;
    defensePercent?: number;
    healthPercent?: number;
    speedPercent?: number;
    attackSpeedPercent?: number;
    abilityPowerPercent?: number;
  };
  // Economy bonuses
  economyBonuses?: {
    extraGoldPerRound?: number;
    interestBonus?: number;
    rerollDiscount?: number;
  };
  // Special effects
  specialEffects?: string[]; // IDs of special effects to apply
}

/**
 * Ruler definition
 */
export interface RulerDefinition {
  id: string;
  name: string;
  title: string;
  description: string;
  passive: RulerPassive;
  color: number; // Theme color for UI
}

/**
 * All ruler definitions
 */
export const RULER_DEFINITIONS: Record<string, RulerDefinition> = {
  // THE WARLORD - Aggressive, damage-focused
  warlord: {
    id: 'warlord',
    name: 'Kira',
    title: 'The Warlord',
    description: 'A fierce commander who leads from the front lines.',
    passive: {
      name: 'Battle Fervor',
      description: 'All units gain +10% Attack and +5% Attack Speed.',
      teamBonuses: {
        attackPercent: 10,
        attackSpeedPercent: 5,
      },
    },
    color: 0xdc2626, // Red
  },

  // THE GUARDIAN - Defensive, survival-focused
  guardian: {
    id: 'guardian',
    name: 'Marcus',
    title: 'The Guardian',
    description: 'A stalwart protector who shields his allies.',
    passive: {
      name: 'Steadfast Defense',
      description: 'All units gain +15% Defense and +10% Max Health.',
      teamBonuses: {
        defensePercent: 15,
        healthPercent: 10,
      },
    },
    color: 0x2563eb, // Blue
  },

  // THE MERCHANT - Economy-focused
  merchant: {
    id: 'merchant',
    name: 'Vera',
    title: 'The Merchant',
    description: 'A cunning trader who always has the upper hand in negotiations.',
    passive: {
      name: 'Golden Touch',
      description: '+2 Gold per round. Rerolls cost 1 less gold.',
      economyBonuses: {
        extraGoldPerRound: 2,
        rerollDiscount: 1,
      },
    },
    color: 0xeab308, // Yellow
  },

  // THE TACTICIAN - Balanced, speed-focused
  tactician: {
    id: 'tactician',
    name: 'Chen',
    title: 'The Tactician',
    description: 'A brilliant strategist who outmaneuvers opponents.',
    passive: {
      name: 'Swift Strategy',
      description: 'All units gain +15% Speed and +10% Attack Speed.',
      teamBonuses: {
        speedPercent: 15,
        attackSpeedPercent: 10,
      },
    },
    color: 0x16a34a, // Green
  },

  // THE ARCHMAGE - Magic-focused
  archmage: {
    id: 'archmage',
    name: 'Elara',
    title: 'The Archmage',
    description: 'A powerful mage who enhances magical abilities.',
    passive: {
      name: 'Arcane Empowerment',
      description: 'All units gain +20% Ability Power.',
      teamBonuses: {
        abilityPowerPercent: 20,
      },
    },
    color: 0x7c3aed, // Purple
  },

  // THE SWARM QUEEN - Quantity over quality
  swarmQueen: {
    id: 'swarmQueen',
    name: 'Zyx',
    title: 'The Swarm Queen',
    description: 'Commands endless hordes with ruthless efficiency.',
    passive: {
      name: 'Endless Swarm',
      description: 'All units gain +20% Attack Speed. +1 Interest per 10 gold.',
      teamBonuses: {
        attackSpeedPercent: 20,
      },
      economyBonuses: {
        interestBonus: 1,
      },
    },
    color: 0x65a30d, // Lime
  },

  // THE SHADOW LORD - Assassin synergy
  shadowLord: {
    id: 'shadowLord',
    name: 'Shade',
    title: 'The Shadow Lord',
    description: 'A master of stealth and assassination.',
    passive: {
      name: 'Death from Shadows',
      description: 'All units gain +8% Attack. First attack deals +25% damage.',
      teamBonuses: {
        attackPercent: 8,
      },
      specialEffects: ['firstStrikeBonus'],
    },
    color: 0x1f2937, // Dark gray
  },

  // THE HEALER QUEEN - Support synergy
  healerQueen: {
    id: 'healerQueen',
    name: 'Seraphina',
    title: 'The Healer Queen',
    description: 'Channels healing light to restore her allies.',
    passive: {
      name: 'Blessing of Light',
      description: 'All units gain +15% Max Health. Units heal 5% max HP at battle start.',
      teamBonuses: {
        healthPercent: 15,
      },
      specialEffects: ['battleStartHeal'],
    },
    color: 0xfbbf24, // Amber
  },
};

/**
 * Get ruler definition by ID
 */
export function getRulerDefinition(rulerId: string): RulerDefinition | undefined {
  return RULER_DEFINITIONS[rulerId];
}

/**
 * Get all ruler IDs
 */
export function getAllRulerIds(): string[] {
  return Object.keys(RULER_DEFINITIONS);
}

/**
 * Get all rulers
 */
export function getAllRulers(): RulerDefinition[] {
  return Object.values(RULER_DEFINITIONS);
}
